"use client";

import { z } from "zod";
import { RegisterEmployeeFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormMessage } from "@/components/ui/form";
import { useTranslations } from "next-intl";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { UserJWTDecoded } from "@/types";
import { useToast } from "@/hooks/use-toast";
import {
  IGetSalonEmployeesByOwnerIdAction,
  IGetSalonServicesByOwnerIdAction,
} from "@/actions/owner/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import hooks from "@/hooks";
import LoadingSpinner from "@/components/core/Spinner";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import MultipleSelector, { Option } from "../Fields/MultipleSelector";
import actions from "@/actions";
import OwnerSalonEmployeesTable from "../../Table/OwnerSalonEmployeesTable";
import { Plus } from "lucide-react";
import { ptBRCountries } from "../../../../consts/PtBRCountries";

type SalonService = z.infer<typeof RegisterEmployeeFormSchema>;

interface IRegisterEmployeeForm {
  user: UserJWTDecoded["user"];
  vocations: IGetSalonServicesByOwnerIdAction["Response"];
  employees: IGetSalonEmployeesByOwnerIdAction["Response"];
}

const RegisterEmployeeForm = ({
  user,
  vocations,
  employees,
}: IRegisterEmployeeForm) => {
  const [employeesState, setEmployeesState] = useState(employees);
  const [employeeFieldLocked, setEmployeeFieldLocked] = useState(true);

  const t = useTranslations("Team.Register");
  const { toast } = useToast();

  const router = useRouter();

  const [forceStackedLayout, setForceStackedLayout] = useState(false);
  const whatsappRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const multiSelectRef = useRef<HTMLDivElement>(null);
  const [valueMultipleSelector, setValueMultipleSelector] = useState<Option[]>(
    [],
  );

  const form = useForm<SalonService>({
    resolver: zodResolver(RegisterEmployeeFormSchema),
    defaultValues: {
      name: "",
      vocation_id: [],
      whatsapp: "+55",
      user_id: "",
    },
  });

  useEffect(() => {
    const whatsappHeight = whatsappRef.current?.offsetHeight || 0;
    const nameHeight = nameRef.current?.offsetHeight || 0;
    const multiSelectHeight = multiSelectRef.current?.offsetHeight || 0;

    const maxOtherHeight = Math.max(whatsappHeight, nameHeight);

    if (multiSelectHeight - maxOtherHeight > 50) {
      setForceStackedLayout(true);
    } else {
      setForceStackedLayout(false);
    }
  }, [form.watch("vocation_id")]);

  const phone = form.watch().whatsapp;

  const userService = hooks.useGetUserByPhone({
    onSuccess: (data) => {
      if (data) {
        form.setValue("name", data.name);
        form.setValue("user_id", data.id);
      }
    },
    onError: () => {
      toast({
        title: "Usuário não encontrado",
        description: t("form.employeeAccountInfo", {
          link: "https://google.com.br",
        }),
        variant: "warning",
      });
      setEmployeeFieldLocked(false);
      form.setValue("user_id", user.userid);
    },
  });

  const fullEmployeeService = hooks.useCreateSalonFullEmployee({
    onSuccess: () => {
      toast({
        title: "Funcionário criado com sucesso",
        description: "O funcionário foi criado com sucesso",
        variant: "success",
      });
      form.reset();
    },
    onError: (err) => {
      toast({
        title: "Falha ao criar funcionário",
        description: err.message || "Não foi possível criar o funcionário",
        variant: "warning",
      });
    },
  });

  const employeeService = hooks.useCreateEmployee({
    onSuccess: async () => {
      toast({
        title: "Funcionário criado com sucesso",
        description: "O funcionário foi criado com sucesso",
        variant: "success",
      });
      form.reset();
    },
    onError: (err) => {
      toast({
        title: "Falha ao criar funcionário",
        description: err.message || "Não foi possível criar o funcionário",
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: SalonService) {
    if (!employeeFieldLocked) return handleEmployeeFullCreation();
    await employeeService.mutateAsync({
      salon_owner_id: user.userid,
      user_id: data.user_id,
      vocation_ids: data.vocation_id,
    });

    setValueMultipleSelector([]);

    const updatedEmployees = await actions.getSalonEmployeesByOwnerIdAction(
      user.userid,
    );

    setEmployeesState(updatedEmployees);
  }

  async function handleEmployeeFullCreation() {
    await fullEmployeeService.mutateAsync({
      name: form.watch("name"),
      phone: form.watch("whatsapp"),
      salon_owner_id: user.userid,
      vocation_ids: form.watch("vocation_id"),
    });
    setValueMultipleSelector([]);
    const updatedEmployees = await actions.getSalonEmployeesByOwnerIdAction(
      user.userid,
    );
    setEmployeesState(updatedEmployees);
  }

  const vocationOptions = Array.isArray(vocations)
    ? vocations.map((vocation) => ({
        label: vocation.name,
        value: vocation.id,
      }))
    : [];

  async function handleWhatsAppOnBlur() {
    userService.mutateAsync({ phone });
  }

  return (
    <TooltipProvider>
      <div className="w-full flex flex-col gap-y-4 items-start justify-between bg-element-light rounded-2xl p-2 md:p-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full flex flex-col items-center justify-center gap-y-4 p-4 sm:p-6"
          >
            {vocationOptions.length > 0 ? (
              <>
                <div
                  className={`w-full flex justify-between items-start gap-4 mx-auto ${forceStackedLayout ? "flex-col" : "2xl:flex-row flex-col"}  mb-4`}
                >
                  <div
                    className="flex flex-col w-full flex-1"
                    ref={whatsappRef}
                  >
                    <label
                      htmlFor="whatsapp"
                      className="text-sm font-semibold mb-1"
                    >
                      {t("form.labels.whatsapp")}
                    </label>
                    <PhoneInput
                      country={"br"}
                      value={form.watch("whatsapp")}
                      onChange={(value) => form.setValue("whatsapp", value)}
                      onBlur={handleWhatsAppOnBlur}
                      enableSearch={true}
                      searchPlaceholder="Buscar"
                      countryCodeEditable={false}
                      localization={{ ...ptBRCountries }}
                      inputStyle={{
                        width: "100%",
                        height: "40px",
                        borderRadius: "9999px",
                        border: "1px solid #e5e7eb",
                        backgroundColor: "transparent",
                        padding: "0.25rem 0.75rem",
                        paddingLeft: "55px",
                        fontSize: "1rem",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.2s",
                        outline: "none",
                      }}
                      containerStyle={{
                        width: "100%",
                      }}
                      buttonStyle={{
                        borderTopLeftRadius: "9999px",
                        borderBottomLeftRadius: "9999px",
                        width: "50px",
                        border: "1px solid rgb(229, 231, 235)",
                        paddingLeft: "6px",
                      }}
                      dropdownStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                      inputProps={{
                        name: "whatsapp",
                        required: true,
                        placeholder: t("form.placeholder.whatsapp"),
                      }}
                    />

                    {form.formState.errors.whatsapp && (
                      <FormMessage>
                        {form.formState.errors.whatsapp.message}
                      </FormMessage>
                    )}
                  </div>

                  <div className="w-full flex-1" ref={nameRef}>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger className="w-full text-left">
                        <FormFields.InputFormField
                          disabled={employeeFieldLocked}
                          form={form}
                          formName="name"
                          label={t("form.labels.name")}
                          placeholder={t("form.placeholder.name")}
                          noClearButton
                          rightElement={
                            userService.isPending ? (
                              <LoadingSpinner size={16} />
                            ) : undefined
                          }
                        />
                      </TooltipTrigger>
                      <TooltipContent className="w-full max-w-96">
                        <p>{t("form.employeeAccountInfo")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="w-full flex-1" ref={multiSelectRef}>
                    <label
                      htmlFor="vocation"
                      className="text-sm font-semibold mb-1"
                    >
                      {t("form.labels.vocation")}
                    </label>
                    <MultipleSelector
                      value={valueMultipleSelector}
                      onChange={(newValue) => {
                        setValueMultipleSelector(newValue);
                        form.setValue(
                          "vocation_id",
                          newValue.map((v) => v.value),
                        );
                      }}
                      defaultOptions={vocationOptions}
                      placeholder="Selecione..."
                      emptyIndicator={"Nenhuma vocação encontrada."}
                      badgeClassName="bg-[#4b5563] text-content-primary  hover:bg-[#35393e]  durantion-300 ease-linear"
                    />
                    {form.formState.errors.vocation_id && (
                      <FormMessage>
                        {form.formState.errors.vocation_id.message}
                      </FormMessage>
                    )}
                  </div>
                </div>

                <div className="w-full flex justify-end mt-2">
                  <Buttons.DefaultButton
                    type="submit"
                    title={t("form.button")}
                    className="w-full sm:w-auto h-12"
                    isLoading={
                      employeeService.isPending || fullEmployeeService.isPending
                    }
                    rightElement={<Plus />}
                  />
                </div>
              </>
            ) : (
              <div className="w-full flex flex-col items-center justify-center p-4 rounded-lg">
                <p className="md:text-lg font-medium">
                  Para continuar, cadastre pelo menos um serviço.
                </p>
                <Buttons.DefaultButton
                  title="Cadastrar Serviço"
                  className="mt-3 bg-red-500 hover:bg-red-600 text-white px-6 py-2 w-50 rounded-full"
                  onClick={() => router.push("/servicos")}
                />
              </div>
            )}
          </form>
        </Form>
      </div>

      <div className="w-full flex flex-col gap-y-4 items-start justify-between bg-element-light rounded-2xl p-6 md:p-8">
        <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-heading-xs">{t("form.labels.equipe")}</h1>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <OwnerSalonEmployeesTable
            data={employeesState}
            availableVocations={vocations}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RegisterEmployeeForm;
